import { Head, router } from '@inertiajs/react'
import { PageHeader, PageContent } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { MoneyDisplay } from '@/components/MoneyDisplay'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Invoice } from '@/types'
import { FileIcon, UploadIcon, FileTextIcon } from 'lucide-react'
import { useRef } from 'react'

interface Props {
  invoices: Invoice[]
}

export default function Index({ invoices }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)

  function handleUpload(e: React.FormEvent) {
    e.preventDefault()
    const file = fileRef.current?.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('csv', file)

    router.post('/invoices', formData, {
      forceFormData: true,
    })
  }

  return (
    <>
      <Head title="Invoices" />
      <PageHeader title="Invoices" description="Manage and track your invoices" />

      <PageContent className="space-y-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-[15px]">Import Invoices</CardTitle>
            <CardDescription className="text-[13px]">
              Generate invoices from a Wise CSV export file
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="flex flex-col gap-3 md:flex-row md:items-end">
              <div className="flex-1 max-w-sm">
                <Label htmlFor="csv" className="text-[13px] font-medium">
                  CSV File
                </Label>
                <Input
                  id="csv"
                  type="file"
                  accept=".csv"
                  ref={fileRef}
                  className="mt-1.5"
                />
              </div>
              <Button type="submit" size="sm" className="gap-1.5">
                <UploadIcon className="h-3.5 w-3.5" />
                Generate Invoices
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {invoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="rounded-full bg-muted p-3.5">
                  <FileTextIcon className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="mt-3 text-[15px] font-medium text-foreground">No invoices yet</h3>
                <p className="mt-1 text-[13px] text-muted-foreground">
                  Import a CSV file to generate invoices.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[13px] font-medium text-muted-foreground">Invoice</TableHead>
                    <TableHead className="text-[13px] font-medium text-muted-foreground">Issued Date</TableHead>
                    <TableHead className="text-[13px] font-medium text-muted-foreground">Payer</TableHead>
                    <TableHead className="text-right text-[13px] font-medium text-muted-foreground">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id} className="group">
                      <TableCell>
                        {invoice.pdf_url ? (
                          <a
                            href={invoice.pdf_url}
                            className="flex items-center gap-2 text-[13px] font-medium text-primary hover:text-primary/80 transition-colors"
                          >
                            <FileIcon className="h-3.5 w-3.5" />
                            {invoice.name}
                          </a>
                        ) : (
                          <span className="text-[13px] font-medium text-foreground">
                            {invoice.name}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-[13px] text-muted-foreground tabular-nums">
                        {new Date(invoice.issued_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </TableCell>
                      <TableCell className="text-[13px] text-muted-foreground">
                        {invoice.payer || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <MoneyDisplay money={invoice.amount} size="sm" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageContent>
    </>
  )
}
